<script lang="ts">
    import { kiosks, kioskCashSummaries, RefreshKiosks, RefreshKioskCashSummaries } from "@frontend/kiosklist";
    import type { KioskCashSummaryDTO, KioskStateDTO } from "../walletapi";
    import { push, querystring } from "svelte-spa-router";
    import { groups, RefreshGroups } from "@frontend/grouplist";
    import { onMount, onDestroy } from "svelte";
    import { translate } from "../Translator";
    import { me } from "@frontend/user";
    import { formatMoney, formatDateTime } from "./Formats";

    const REFRESH_INTERVAL_MS = 5000;

    // Kiosk cash event types (Cashier.Model.KioskCashEventType) and their labels.
    const EVENT_LABELS = {
        UserDeposit: 'Player deposit',
        UserWithdraw: 'Player withdrawal',
        AdminDeposit: 'Admin deposit',
        AdminWithdraw: 'Admin withdrawal',
        EmptyRecycler: 'Recycler emptied',
        CollectCashbox: 'Cashbox collected',
        UnconfirmedNote: 'Unconfirmed note',
        UncreditedNote: 'Uncredited note',
        Replenish: 'Replenished',
    } as const;

    type KioskRow = {
        KioskId: string;
        State: KioskStateDTO | undefined;
        Summary: KioskCashSummaryDTO | undefined;
    };

    let searchInput: string = "";
    let searchKiosks: KioskRow[] = [];
    let expandedKioskId: string | undefined = undefined;
    let refreshTimer: ReturnType<typeof setInterval> | undefined;

    let startDate = new Date().toISOString().split('T')[0];
    let endDate = new Date().toISOString().split('T')[0];
    let loadingSummary = false;
    let summaryError: string | undefined = undefined;

    onMount(async () => {
        if (!$me) {
            push("/user/Login");
            return;
        }

        await Promise.all([RefreshKiosks(), loadSummary(), RefreshGroups()]);
        refreshTimer = setInterval(RefreshKiosks, REFRESH_INTERVAL_MS);
    });

    onDestroy(() => {
        if (refreshTimer) {
            clearInterval(refreshTimer);
        }
    });

    async function loadSummary() {
        loadingSummary = true;
        summaryError = await RefreshKioskCashSummaries(new Date(startDate + 'T00:00:00'), new Date(endDate + 'T23:59:59'));
        loadingSummary = false;
    }

    // Connected kiosks and kiosks with cash events in the period, which may be offline now.
    function buildRows(states: KioskStateDTO[], summaries: KioskCashSummaryDTO[]): KioskRow[] {
        let rows = new Map<string, KioskRow>();
        for (let state of states) {
            if (!state.KioskId) continue;
            rows.set(state.KioskId, { KioskId: state.KioskId, State: state, Summary: undefined });
        }
        for (let summary of summaries) {
            if (!summary.KioskId) continue;
            let row = rows.get(summary.KioskId);
            if (row) {
                row.Summary = summary;
            } else {
                rows.set(summary.KioskId, { KioskId: summary.KioskId, State: undefined, Summary: summary });
            }
        }
        return [...rows.values()].sort((a, b) => a.KioskId.localeCompare(b.KioskId));
    }

    $: allKiosks = buildRows($kiosks, $kioskCashSummaries);

    // Only this location's kiosks, from ?group= (the Locations page links here).
    $: groupFilter = readGroup($querystring);

    function readGroup(query: string | undefined): number | undefined {
        const groupId = Number(new URLSearchParams(query ?? '').get('group'));
        return Number.isSafeInteger(groupId) && groupId > 0 ? groupId : undefined;
    }

    function selectGroup(event: Event) {
        const value = Number((event.target as HTMLSelectElement).value);
        push(value > 0 ? `/KioskList?group=${value}` : '/KioskList');
    }

    $: groupOptions = [...$groups].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''));

    $: {
        let rows = groupFilter === undefined
            ? allKiosks
            : allKiosks.filter((kiosk) => kiosk.State?.GroupId === groupFilter);
        if (searchInput === '') {
            searchKiosks = rows;
        } else {
            let searchInputNormalized = searchInput.toLowerCase();
            searchKiosks = rows.filter((kiosk) =>
                kiosk.KioskId.toLowerCase().includes(searchInputNormalized)
            );
        }
    }

    $: periodIn = searchKiosks.reduce((sum, k) => sum + (k.Summary?.InCents ?? 0), 0);
    $: periodOut = searchKiosks.reduce((sum, k) => sum + (k.Summary?.OutCents ?? 0), 0);

    function totalCents(kiosk: KioskStateDTO): number {
        return (kiosk.BillAcceptor?.Levels ?? []).reduce(
            (sum, level) => sum + (level.Value?.InCents ?? 0) * level.Count, 0
        );
    }

    function eventLabel(eventType: string | undefined, tr: (key: (typeof EVENT_LABELS)[keyof typeof EVENT_LABELS]) => string): string {
        let key = EVENT_LABELS[eventType as keyof typeof EVENT_LABELS];
        return key ? tr(key) : eventType ?? "";
    }

    function toggleExpanded(kioskId: string) {
        expandedKioskId = expandedKioskId === kioskId ? undefined : kioskId;
    }
</script>

<h1 class="ui centered dividing header">{$translate('Kiosks')}</h1>

<div class="ui form">
    <div class="inline fields">
        <div class="field">
            <label>{$translate('Start Date')}</label>
            <input type="date" bind:value={startDate} />
        </div>
        <div class="field">
            <label>{$translate('End Date')}</label>
            <input type="date" bind:value={endDate} />
        </div>
        <div class="field">
            <button
                type="button"
                class="ui primary button"
                class:loading={loadingSummary}
                disabled={!startDate || !endDate || loadingSummary}
                on:click={loadSummary}
            >
                <i class="search icon"></i>
                {$translate('Search')}
            </button>
        </div>
        <div class="field">
            <div class="ui icon input">
                <input
                    type="text"
                    placeholder="Search kiosks..."
                    bind:value={searchInput}
                >
                <i class="search icon"></i>
            </div>
        </div>
        <div class="field">
            <select class="ui dropdown" value={groupFilter ?? ''} on:change={selectGroup} aria-label={$translate('Location')}>
                <option value="">{$translate('All locations')}</option>
                {#each groupOptions as group (group.Id)}
                    <option value={group.Id}>{group.Name}</option>
                {/each}
            </select>
        </div>
    </div>
</div>

{#if summaryError}
    <div class="ui negative message">{summaryError}</div>
{/if}

<table class="ui celled table">
    <thead>
        <tr>
            <th>{$translate('Kiosk Id')}</th>
            <th>{$translate('Location')}</th>
            <th>{$translate('Connected')}</th>
            <th>{$translate('Currency Code')}</th>
            <th class="right aligned">{$translate('Cash in kiosk')}</th>
            <th class="right aligned">{$translate('Cash In')}</th>
            <th class="right aligned">{$translate('Cash Out')}</th>
            <th class="right aligned">{$translate('Net')}</th>
            <th>{$translate('Last Seen')}</th>
            <th>{$translate('Options')}</th>
        </tr>
    </thead>
    <tbody>
        {#if searchKiosks.length === 0}
            <tr>
                <td colspan="10" class="center aligned">{$translate('No kiosks connected')}</td>
            </tr>
        {/if}
        {#each searchKiosks as kiosk (kiosk.KioskId)}
            <tr>
                <td>{kiosk.KioskId}</td>
                <td>{kiosk.State?.GroupName ?? ""}</td>
                <td>
                    {#if !kiosk.State?.Online}
                        <span class="ui grey label">{$translate('Offline')}</span>
                    {:else if kiosk.State?.BillAcceptor?.Connected}
                        <span class="ui green label">{$translate('Connected')}</span>
                    {:else}
                        <span class="ui red label">{$translate('Disconnected')}</span>
                    {/if}
                    {#if kiosk.State?.BillAcceptor?.Enabled}
                        <span class="ui blue label">{$translate('Enabled')}</span>
                    {/if}
                </td>
                <td>{kiosk.State?.BillAcceptor?.Currency ?? ""}</td>
                <td class="right aligned">{kiosk.State ? formatMoney(totalCents(kiosk.State)) : ""}</td>
                <td class="right aligned">{formatMoney(kiosk.Summary?.InCents ?? 0)}</td>
                <td class="right aligned">{formatMoney(kiosk.Summary?.OutCents ?? 0)}</td>
                <td class="right aligned">{formatMoney(kiosk.Summary?.NetCents ?? 0)}</td>
                <td>{kiosk.State ? formatDateTime(kiosk.State.LastSeen) : ""}</td>
                <td class="center aligned">
                    <button
                        type="button"
                        class="ui icon primary button"
                        on:click={() => toggleExpanded(kiosk.KioskId)}
                    >
                        <i class="list icon"></i>
                    </button>
                </td>
            </tr>
            {#if expandedKioskId === kiosk.KioskId}
                <tr>
                    <td colspan="10">
                        {#if kiosk.State?.Meters}
                            <h4 class="ui header">
                                {$translate('Meters')}
                                <div class="sub header">
                                    {$translate('Counting since')} {formatDateTime(kiosk.State.MetersStarted)}
                                    · {$translate('Reported')} {formatDateTime(kiosk.State.MetersTime)}
                                </div>
                            </h4>
                            <table class="ui very compact celled table">
                                <thead>
                                    <tr>
                                        <th>{$translate('Row')}</th>
                                        <th class="right aligned">{$translate('In')}</th>
                                        <th class="right aligned">{$translate('Out')}</th>
                                        <th class="right aligned">{$translate('Cash')}</th>
                                        <th class="right aligned">{$translate('Notes / events')}</th>
                                        <th class="right aligned">{$translate('Float target')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each kiosk.State.Meters as meter}
                                        <tr class:warning={meter.Low}>
                                            <td>{meter.Row ?? ""}{meter.Drum ? ` (${meter.Drum})` : ""}</td>
                                            <td class="right aligned">{formatMoney(meter.In)}</td>
                                            <td class="right aligned">{formatMoney(meter.Out)}</td>
                                            <td class="right aligned">{formatMoney(meter.Cash)}</td>
                                            <td class="right aligned">{meter.Count}</td>
                                            <td class="right aligned">{meter.FloatTarget > 0 ? meter.FloatTarget : ""}</td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        {/if}

                        <h4 class="ui header">{$translate('Cash events')}</h4>
                        {#if (kiosk.Summary?.Events ?? []).length === 0}
                            <em>{$translate('No cash events in this period')}</em>
                        {:else}
                            <table class="ui very compact celled table">
                                <thead>
                                    <tr>
                                        <th>{$translate('Event')}</th>
                                        <th>{$translate('Count')}</th>
                                        <th class="right aligned">{$translate('Total')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each kiosk.Summary?.Events ?? [] as event}
                                        <tr>
                                            <td>{eventLabel(event.EventType, $translate)}</td>
                                            <td>{event.Count}</td>
                                            <td class="right aligned">{formatMoney(event.TotalCents)}</td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        {/if}

                        <!-- Kiosks built before they sent meters: just the note levels. -->
                        {#if !kiosk.State?.Meters}
                        <h4 class="ui header">{$translate('Cash in kiosk')}</h4>
                        {#if (kiosk.State?.BillAcceptor?.Levels ?? []).length === 0}
                            <em>{$translate('No denomination data')}</em>
                        {:else}
                            <table class="ui very compact celled table">
                                <thead>
                                    <tr>
                                        <th>{$translate('Denomination')}</th>
                                        <th>{$translate('Count')}</th>
                                        <th>{$translate('Subtotal')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each kiosk.State?.BillAcceptor?.Levels ?? [] as level}
                                        <tr>
                                            <td>{formatMoney(level.Value)}</td>
                                            <td>{level.Count}</td>
                                            <td>{formatMoney((level.Value?.InCents ?? 0) * level.Count)}</td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        {/if}
                        {/if}
                    </td>
                </tr>
            {/if}
        {/each}
    </tbody>
    {#if $kioskCashSummaries.length > 0}
        <tfoot>
            <tr>
                <th colspan="5">{$translate('Total')}</th>
                <th class="right aligned">{formatMoney(periodIn)}</th>
                <th class="right aligned">{formatMoney(periodOut)}</th>
                <th class="right aligned">{formatMoney(periodIn - periodOut)}</th>
                <th colspan="2"></th>
            </tr>
        </tfoot>
    {/if}
</table>
